import { useNavigate } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { DENTAL_CATEGORY_OPTIONS } from 'lib/constants/billing/dentalCategories';

import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';
import { currentPractice } from 'components/practice/practiceState';
import ColorPicker from 'components/shared/ColorPicker/ColorPicker';
import ImageUploader from 'components/shared/ImageUploader/ImageUploader';

import Wrapper from '../Wrapper/Wrapper';
import { handleCDTSearch, handleCPTSearch } from '../../lib/helpers';

const MainSection = ({ formik, service }) => {
  const navigate = useNavigate();
  const practice = useRecoilValue(currentPractice);
  const isDental = practice?.kind === 'dental';

  const selectCode = (event) => {
    if (!event) return;
    const parAmount = Number(event.par_amount ?? 0).toFixed(2);

    formik.setFieldValue('procedure_code', event.value);
    formik.setFieldValue(
      'description',
      event.description || event.meta?.name || formik.values.description
    );
    formik.setFieldValue('total_cost_cents', parAmount);

    if (isDental) {
      formik.setFieldValue('standard_charge', parAmount);
      if (event.dental_category) {
        formik.setFieldValue('dental_category', event.dental_category);
      }
    } else {
      formik.setFieldValue(
        'standard_charge',
        parseFloat(
          parAmount *
            (practice?.display_settings?.billing?.feeSchedule?.medicarePercentage / 100 || 1)
        ).toFixed()
      );
    }
  };

  return (
    <Wrapper isColumn>
      <div className="grid grid-cols-2 items-start gap-4">
        {/* Left column: Color, Name, and Procedure Code */}
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-4">
            <ColorPicker
              buttonClassName="h-[39px]"
              label="Color"
              color={formik.values.color}
              onChange={(color) => formik.setFieldValue('color', color)}
            />
            <Input
              label="Name"
              value={formik.values.name}
              name="name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && formik.errors.name}
              className="flex flex-1 flex-col"
              id="serviceNameInput"
              data-qa="name-input"
              placeholder="Enter service name"
            />
          </div>
          <Select
            id="procedure-code"
            label={isDental ? 'CDT code' : 'Procedure code'}
            value={{ value: formik.values?.procedure_code, label: formik.values?.procedure_code }}
            isAsync
            parentClassName="flex flex-col"
            placeholder={isDental ? 'Search CDT code (e.g. D1110)' : 'Enter procedure code'}
            isClearable={false}
            noOptionMessage={() =>
              isDental
                ? 'Start typing to search CDT codes'
                : 'Start typing to search procedure codes'
            }
            onChange={selectCode}
            loadOptions={(searchTerm) =>
              (isDental ? handleCDTSearch : handleCPTSearch)(searchTerm, navigate)
            }
            disabled={formik.values?.procedure_code && !!service}
          />
          {isDental && (
            <Select
              required
              id="dental-category"
              label="Service category"
              options={DENTAL_CATEGORY_OPTIONS}
              parentClassName="flex flex-col"
              placeholder="Select category"
              onBlur={() => formik.setFieldTouched('dental_category', true)}
              error={formik.touched.dental_category && formik.errors.dental_category}
              onChange={(opt) => formik.setFieldValue('dental_category', opt?.value || null)}
              value={DENTAL_CATEGORY_OPTIONS.find(
                (opt) => opt.value === formik.values.dental_category
              )}
            />
          )}
        </div>

        {/* Right column: Upload Image */}
        <div className="flex items-center justify-center">
          <ImageUploader
            handlers={[formik.values.image]}
            onUpload={(file) => {
              formik.setFieldValue('image', file.handle);
            }}
            type="image"
            className="items-center justify-center"
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default MainSection;
